const LAYOUT_ALT_TOKENS = new Set([
  'full_px',
  'full',
  'inset',
  'natural',
  'grid_1',
  'grid_2',
  'grid_3',
  'grid_4',
  'grid_full',
  'grid_cover_fit',
]);

const GC_GRID_ALT_TOKENS = new Set(['grid_1', 'grid_2', 'grid_3', 'grid_4', 'grid_full', 'grid_cover_fit']);

export const shouldUseInsetPhotoLayout = (alt) => {
  const trimmed = typeof alt === 'string' ? alt.trim().toLowerCase() : '';
  return LAYOUT_ALT_TOKENS.has(trimmed) && !GC_GRID_ALT_TOKENS.has(trimmed);
};

export const shouldUseGcPhotoGridFullLayout = (alt) => {
  const trimmed = typeof alt === 'string' ? alt.trim().toLowerCase() : '';
  return trimmed === 'grid_full';
};

export const shouldUseGcPhotoGridCoverFitLayout = (alt) => {
  const trimmed = typeof alt === 'string' ? alt.trim().toLowerCase() : '';
  return trimmed === 'grid_cover_fit';
};

export const sliceUsesInsetPhotoLayout = (photos) =>
  Array.isArray(photos) && photos.some((photo) => shouldUseInsetPhotoLayout(photo?.image?.alt));

export const getGcPhotoGridRole = (alt) => {
  const trimmed = typeof alt === 'string' ? alt.trim().toLowerCase() : '';
  if (trimmed === 'grid_1') return 1;
  if (trimmed === 'grid_2') return 2;
  return null;
};

/** Two-photo grid when one image alt is grid_1 and the other is grid_2. */
export const shouldUseGcPhotoGridLayout = (photos) => {
  if (!Array.isArray(photos) || photos.length !== 2) return false;

  const roles = photos.map((photo) => getGcPhotoGridRole(photo?.image?.alt));
  return roles.includes(1) && roles.includes(2);
};

export const getGcPhotoGridClassName = (alt, block = 'media') => {
  const role = getGcPhotoGridRole(alt);
  if (role === 1) return `gc-read-photos-pair__${block}--grid-1`;
  if (role === 2) return `gc-read-photos-pair__${block}--grid-2`;
  return '';
};

/** wide-narrow = 7fr/3fr; narrow-wide = 3fr/7fr when first photo in slice is grid_2. */
export const getGcPhotoGridLayoutModifier = (photos) => {
  if (!Array.isArray(photos)) return 'wide-narrow';

  const first = photos.find((photo) => photo?.image?.fluid?.src);
  return getGcPhotoGridRole(first?.image?.alt) === 2 ? 'narrow-wide' : 'wide-narrow';
};

export const getGcPhotoTripleRole = (alt) => {
  const trimmed = typeof alt === 'string' ? alt.trim().toLowerCase() : '';
  if (trimmed === 'grid_3') return 3;
  if (trimmed === 'grid_4') return 4;
  return null;
};

/** Three-photo grid: one grid_3 (tall) + two grid_4 (stacked). */
export const shouldUseGcPhotoTripleLayout = (photos) => {
  if (!Array.isArray(photos) || photos.length !== 3) return false;

  const roles = photos.map((photo) => getGcPhotoTripleRole(photo?.image?.alt));
  return roles.filter((role) => role === 3).length === 1 && roles.filter((role) => role === 4).length === 2;
};

export const getGcPhotoTripleItems = (photos) => {
  if (!Array.isArray(photos)) return { stack: [], tall: null };

  const valid = photos.filter((photo) => photo?.image?.fluid?.src);
  const tall = valid.find((photo) => getGcPhotoTripleRole(photo?.image?.alt) === 3) || null;
  const stack = valid.filter((photo) => getGcPhotoTripleRole(photo?.image?.alt) === 4);

  return { stack, tall };
};

/** tall-right (default) or tall-left based on whether grid_3 appears before grid_4 items. */
export const getGcPhotoTripleLayoutModifier = (photos) => {
  if (!Array.isArray(photos)) return 'tall-right';

  const valid = photos.filter((photo) => photo?.image?.fluid?.src);
  const tallIndex = valid.findIndex((photo) => getGcPhotoTripleRole(photo?.image?.alt) === 3);
  const firstStackIndex = valid.findIndex((photo) => getGcPhotoTripleRole(photo?.image?.alt) === 4);

  if (tallIndex === -1 || firstStackIndex === -1) return 'tall-right';
  return tallIndex < firstStackIndex ? 'tall-left' : 'tall-right';
};

export const getPhotoImgAlt = (alt, caption) => {
  const trimmed = typeof alt === 'string' ? alt.trim() : '';
  if (!trimmed) return '';
  if (LAYOUT_ALT_TOKENS.has(trimmed.toLowerCase())) {
    return caption || '';
  }
  return trimmed;
};
